import React, { useCallback } from "react";
import {
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  makeStyles,
  Theme,
} from "@material-ui/core";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) => ({
  visuallyHiddenInput: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  previewContainer: {
    position: "relative",
    display: "inline-block",
    marginTop: theme.spacing(2),
  },
  previewImage: {
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  deleteButton: {
    position: "absolute",
    top: -8,
    right: -8,
    backgroundColor: theme.palette.error.main,
    color: "white",
    width: 24,
    height: 24,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  placeholderBox: {
    width: 200,
    height: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: theme.palette.grey[300],
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
  },
}));

interface FileUploadProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  label?: string;
  accept?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  label = "อัพโหลดรูปภาพ",
  accept = "image/*",
}) => {
  const classes = useStyles();
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // สำหรับตอนนี้ใช้ FileReader เพื่อแปลงเป็น base64
        // ในอนาคตจะเปลี่ยนเป็นการอัพโหลดจริง
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          onChange(result);
        };
        reader.readAsDataURL(file);
      }
    },
    [onChange]
  );

  const handleRemove = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const handleUploadAreaClick = () => {
    const input = document.getElementById("file-upload-input");
    input?.click();
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {!value ? (
        <Paper className={classes.uploadArea} onClick={handleUploadAreaClick}>
          <CloudUploadIcon
            style={{ fontSize: 48, color: "#9e9e9e", marginBottom: 16 }}
          />
          <Typography variant="body1" color="textSecondary" gutterBottom>
            {"คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวางที่นี่"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {"รองรับไฟล์ภาพ (JPG, PNG, GIF)"}
          </Typography>
          <input
            id="file-upload-input"
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className={classes.visuallyHiddenInput}
          />
        </Paper>
      ) : (
        <Box>
          <Box className={classes.previewContainer}>
            {value.startsWith("data:image") ? (
              <img src={value} alt="Preview" className={classes.previewImage} />
            ) : (
              <Box className={classes.placeholderBox}>
                <ImageIcon style={{ fontSize: 48, color: "#9e9e9e" }} />
              </Box>
            )}
            <IconButton
              size="small"
              onClick={handleRemove}
              className={classes.deleteButton}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box mt={2}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadAreaClick}
              size="small"
            >
              {" เปลี่ยนรูปภาพ"}
            </Button>
            <input
              id="file-upload-input"
              type="file"
              accept={accept}
              onChange={handleFileChange}
              className={classes.visuallyHiddenInput}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
